import { searchPinActions, mapActions } from "../reducers/mapPanZoomReducer.js";

/**
 * フロア関連のミュータブルな内部状態を保持するストアを生成
 * @returns {{
 *   stashedFloorsByKey: Map<string, SVGElement>,
 *   mountedFloorId: string | null,
 *   stashedCampuses: Set<number>,
 *   floorToBuildingCache: Map<string, string>
 * }} フロア管理用ストア
 */
export function createFloorStore() {
  return {
    stashedFloorsByKey: new Map(),
    mountedFloorId: null,
    stashedCampuses: new Set(),
    floorToBuildingCache: new Map(),
  };
}

/**
 * SVGルートにindoor-layerコンテナを確保する
 * @param {SVGElement | null} svgRoot SVGルート要素
 * @returns {SVGGElement | null} indoor-layer要素
 */
export function ensureIndoorLayerContainer(svgRoot) {
  if (!svgRoot) return null;
  let container = svgRoot.querySelector("#indoor-layer");

  if (!container) {
    container = document.createElementNS("http://www.w3.org/2000/svg", "g");
    container.setAttribute("id", "indoor-layer");
    svgRoot.appendChild(container);
  }

  return container;
}

/**
 * 建物のフロア要素をDOMから外してfloorStoreに保存する
 * @param {object} floorStore フロアストア
 * @param {number} currentCampus 現在のキャンパス
 * @param {Array} mapJson マップJSONデータ
 */
export function stashFloors(floorStore, currentCampus, mapJson) {
  const campusIdx = currentCampus - 1;
  const currentMapJson = mapJson[campusIdx] || {};
  const buildingKeys = Object.keys(currentMapJson);

  buildingKeys.forEach((key) => {
    const building = currentMapJson[key];
    if (!building?.indoor || !Array.isArray(building.floors)) return;

    building.floors.forEach((f) => {
      const floorElement = document.getElementById(f.floor_id);
      if (!floorElement || !floorElement.parentNode) return;
      floorElement.parentNode.removeChild(floorElement);
      const uniqueFloorKey = `${campusIdx}_${f.floor_id}`;
      if (!floorStore.stashedFloorsByKey.has(uniqueFloorKey)) {
        floorStore.stashedFloorsByKey.set(uniqueFloorKey, floorElement);
      }
    });
  });

  floorStore.stashedCampuses.add(campusIdx);
}

/**
 * 屋内レイヤーをリセットし、表示中のフロアをストアへ戻す
 * @param {object} floorStore フロアストア
 * @param {SVGElement | null} svgRoot SVGルート要素
 * @param {number} currentCampus 現在のキャンパス
 */
export function resetIndoorLayer(floorStore, svgRoot, currentCampus) {
  const container = ensureIndoorLayerContainer(svgRoot);
  if (!container) return;

  while (container.firstChild) {
    const floorElement = container.firstChild;
    const id = floorElement.getAttribute?.("id");
    container.removeChild(floorElement);
    if (id) {
      const campusIdx = currentCampus - 1;
      const uniqueFloorKey = `${campusIdx}_${id}`;
      floorStore.stashedFloorsByKey.set(uniqueFloorKey, floorElement);
    }
  }
  floorStore.mountedFloorId = null;
}

/**
 * 指定フロアをDOMに表示する
 * @param {object} floorStore フロアストア
 * @param {SVGElement | null} svgRoot SVGルート要素
 * @param {number} currentCampus 現在のキャンパス
 * @param {string | null | undefined} floorId 表示するフロアID
 */
export function showFloor(floorStore, svgRoot, currentCampus, floorId) {
  const container = ensureIndoorLayerContainer(svgRoot);
  if (!container || !floorId) return;

  resetIndoorLayer(floorStore, svgRoot, currentCampus);

  const campusIdx = currentCampus - 1;
  const uniqueFloorKey = `${campusIdx}_${floorId}`;
  const floorElement = floorStore.stashedFloorsByKey.get(uniqueFloorKey);
  if (!floorElement) return;

  container.appendChild(floorElement);
  floorStore.stashedFloorsByKey.delete(uniqueFloorKey);
  floorStore.mountedFloorId = floorId;
}

/**
 * フロアIDから建物IDを引くためのキャッシュを構築
 * @param {object} floorStore フロアストア
 * @param {string[]} currentBuildingArray 建物配列
 * @param {Record<string, object>} currentMapJson マップJSON
 */
export function buildFloorToBuildingCache(
  floorStore,
  currentBuildingArray,
  currentMapJson,
) {
  floorStore.floorToBuildingCache.clear();
  for (const buildingKey of currentBuildingArray) {
    const building = currentMapJson[buildingKey];
    if (building?.floors && building.building_id) {
      for (const floor of building.floors) {
        if (floor.floor_id) {
          floorStore.floorToBuildingCache.set(
            floor.floor_id,
            building.building_id,
          );
        }
      }
    }
  }
}

/**
 * 現在表示中のフロアの建物IDを取得
 * @param {object} floorStore フロアストア
 * @returns {string | null} 建物ID
 */
export function getCurrentBuildingId(floorStore) {
  const currentFloorId = floorStore.mountedFloorId;
  if (!currentFloorId) return null;
  return floorStore.floorToBuildingCache.get(currentFloorId) ?? null;
}

/**
 * 検索ピンの表示/非表示をフロア変更に応じて更新
 * @param {object} floorStoreRef フロアストアref
 * @param {function} dispatch 統合dispatch
 * @param {object} searchPin 検索ピンのstate
 * @param {string} currentFloorId 現在表示中のフロアID
 */
export function updateSearchPinVisibility(
  floorStoreRef,
  dispatch,
  searchPin,
  currentFloorId,
) {
  const floorStore = floorStoreRef.current;

  if (
    searchPin.floorId !== null &&
    searchPin.buildingId !== null &&
    searchPin.x !== null &&
    searchPin.y !== null
  ) {
    const currentBuildingId = getCurrentBuildingId(floorStore);
    if (currentBuildingId === searchPin.buildingId) {
      dispatch(
        searchPinActions.setVisible(currentFloorId === searchPin.floorId),
      );
    } else {
      dispatch(searchPinActions.setVisible(true));
    }
  }
}

/**
 * フロア変更時のイベントハンドラ
 * @param {object} floorStoreRef フロアストアref
 * @param {object} svgRootRef SVGルートref
 * @param {function} dispatch 統合dispatch
 * @param {object} mapSlice currentCampus, currentSelectedFloors
 * @param {object} searchPin 検索ピンのstate
 * @param {number} changeToIndex 遷移先フロアの配列インデックス
 */
export function changeFloor(
  floorStoreRef,
  svgRootRef,
  dispatch,
  mapSlice,
  searchPin,
  changeToIndex,
) {
  const floorStore = floorStoreRef.current;
  const svgRoot = svgRootRef.current;
  const { currentCampus, currentSelectedFloors } = mapSlice;

  dispatch(mapActions.setFloorIndex(changeToIndex));
  const target = currentSelectedFloors[changeToIndex];
  if (target?.floor_id) {
    showFloor(floorStore, svgRoot, currentCampus, target.floor_id);
    updateSearchPinVisibility(
      floorStoreRef,
      dispatch,
      searchPin,
      target.floor_id,
    );
  }
}
